import type { Edge, Node } from "@xyflow/react";

import {
  calculateApiNodeId,
  findAllForEachAncestors,
  type InvalidVariable,
  type NodeDefinition,
  type WorkflowVariable,
} from "./extract-workflow-variables";
import { extractWorkflowVariables } from "./extract-workflow-variables-flat";

// ============================================================================
// WORKFLOW VARIABLE VALIDATION
// ============================================================================

/**
 * Parse a variable reference like "{{nodeId.result.path.to.field}}" into components.
 * Returns null if the format doesn't match.
 */
function parseVariableReference(varRef: string): {
  nodeId: string;
  section: string;
  path: string;
} | null {
  // Remove {{ and }}
  const inner = varRef.slice(2, -2);
  const parts = inner.split(".");

  if (parts.length < 2) return null;

  const nodeId = parts[0];
  const section = parts[1]; // "result" or "context"
  const path = parts.slice(2).join(".");

  return { nodeId, section, path };
}

/**
 * Check if validation should be skipped for a variable reference.
 * Matches backend logic in definition_variable_reference.go:
 * - Skip payload.* if top-level field exists (allows nested access on config values)
 * - Skip evm-log params.* (dynamic based on ABI)
 * - Skip evm-log raw.* (nested known fields)
 * - Skip webhook body.* if no x402 schema defined (free-form)
 * - Skip webhook payment.* (nested known fields)
 *
 * @param varRef - The variable reference string (e.g., "{{root.result.params.from}}")
 * @param apiNodeIdToNodeMap - Pre-calculated map of API node IDs to Node objects for O(1) lookup
 */
function shouldSkipValidation(varRef: string, apiNodeIdToNodeMap: Map<string, Node>): boolean {
  const parsed = parseVariableReference(varRef);
  if (!parsed) return false;

  const { nodeId, section, path } = parsed;

  // Look up the referenced node by its API node ID (O(1) lookup)
  const referencedNode = apiNodeIdToNodeMap.get(nodeId);
  if (!referencedNode) return false;

  // For payload section: allow nested access if the top-level field exists
  // e.g., {{root.payload.config.someNestedField}} is valid if "config" exists in payload
  if (section === "payload") {
    const nodePayload = referencedNode.data.payload as Record<string, unknown> | undefined;
    if (!nodePayload) return false;

    // Extract top-level field name from path
    const topLevelField = path.split(".")[0];
    if (topLevelField && topLevelField in nodePayload) {
      return true; // Skip validation - field exists, nested access is allowed
    }
    return false;
  }

  // Skip validation for "input" section references (e.g., {{root.input.userId}}).
  // Input schema fields are user-defined and validated at runtime.
  if (section === "input") return true;

  // Only continue checking result section rules below
  if (section !== "result") return false;

  const nodeType = (referencedNode.data.type as string) || "";

  // Skip validation for evm-log trigger's dynamic/nested paths
  if (nodeType === "evm-log") {
    // params.* is dynamic (decoded from ABI)
    if (path.startsWith("params.") || path === "params") {
      return true;
    }
    // raw.* is nested (transactionHash, blockNumber, etc.)
    if (path.startsWith("raw.") || path === "raw") {
      return true;
    }
  }

  // Skip validation for token-price-cexes trigger's nested paths
  if (nodeType === "token-price-cexes") {
    // These have nested result structures
    if (path.startsWith("price.") || path === "price") {
      return true;
    }
  }

  // Skip validation for call-x402-endpoint action's dynamic paths
  // The "resource" field has additionalProperties: true (any nested access is valid)
  if (nodeType === "call-x402-endpoint") {
    if (path.startsWith("resource.") || path === "resource") {
      return true;
    }
  }

  return false;
}

/**
 * Extract all variable references from a value (string or nested object)
 */
function extractVariableReferences(value: unknown): string[] {
  const variables: string[] = [];
  const regex = /\{\{[^}]+\}\}/g;

  function traverse(val: unknown) {
    if (typeof val === "string") {
      const matches = val.match(regex);
      if (matches) {
        variables.push(...matches);
      }
    } else if (Array.isArray(val)) {
      val.forEach(item => traverse(item));
    } else if (val && typeof val === "object") {
      Object.values(val).forEach(v => traverse(v));
    }
  }

  traverse(value);
  return variables;
}

/**
 * Validate all variable references in a workflow
 * Returns a list of invalid variables that don't exist in parent nodes' outputs
 */
export function validateWorkflowVariables(
  allNodes: Node[],
  allEdges: Edge[],
  nodeDefinitions?: NodeDefinition[],
): InvalidVariable[] {
  const invalidVariables: InvalidVariable[] = [];

  // Cache for node output variables to avoid redundant computations
  const variablesCache = new Map<string, WorkflowVariable[]>();

  // Find root node (node with no incoming edges)
  const incomingEdges = new Set(allEdges.map(edge => edge.target));
  const rootNode = allNodes.find(n => !incomingEdges.has(n.id));

  // Pre-calculate API node ID to Node map for O(1) lookups in shouldSkipValidation
  // This avoids O(N²) complexity when validating variable references
  const apiNodeIdToNodeMap = new Map<string, Node>();
  allNodes.forEach(n => {
    const apiNodeId = calculateApiNodeId(n, allNodes, allEdges);
    apiNodeIdToNodeMap.set(apiNodeId, n);
  });

  // Check each non-root node for invalid variable references
  allNodes.forEach(node => {
    // Skip root node (triggers don't reference other nodes)
    if (rootNode && node.id === rootNode.id) {
      return;
    }

    const nodeType = (node.data.type as string) || "";
    const nodeName = (node.data.name as string) || nodeType;
    const payload = node.data.payload as Record<string, unknown> | undefined;

    if (!payload) {
      return;
    }

    // Get available variables for this node (with caching for performance)
    const availableVariables = extractWorkflowVariables(node.id, allNodes, allEdges, nodeDefinitions, variablesCache);
    const availableVarIds = new Set(availableVariables.map(v => v.id));

    // Check if this node is inside a for-each loop to allow loop variable access
    const forEachAncestors = findAllForEachAncestors(node.id, allNodes, allEdges);
    const forEachAncestor = forEachAncestors.length > 0 ? forEachAncestors[0] : null;
    const forEachPayload = forEachAncestor?.data.payload as { itemVar?: string; indexVar?: string } | undefined;
    const itemVar = forEachPayload?.itemVar || "$item";
    const indexVar = forEachPayload?.indexVar || "$index";

    // Check each field in the payload for variable references
    Object.entries(payload).forEach(([fieldName, fieldValue]) => {
      const variableRefs = extractVariableReferences(fieldValue);

      variableRefs.forEach(varRef => {
        // Skip template variables (prefixed with @).
        // Template variables like {{@field}} are used in format action's template field
        // to access properties of the data object. They are resolved at runtime by the
        // action executor, not validated here.
        const varContent = varRef.slice(2, -2); // Remove {{ and }}
        if (varContent.startsWith("@")) {
          return;
        }

        // Skip props variables (prefixed with $props).
        // Props variables like {{$props.myKey}} reference template-level user
        // configurable fields. Values are supplied when instantiating from a template.
        if (varContent.startsWith("$props.")) {
          return;
        }

        // Skip validation for dynamic paths (evm-log params, webhook body without schema, etc.)
        if (shouldSkipValidation(varRef, apiNodeIdToNodeMap)) {
          return;
        }

        // Check if this variable exists in available variables (exact match required)
        if (availableVarIds.has(varRef)) {
          return; // Valid - exact match
        }

        // Check if this is a nested access on a known parent variable
        // If {{root.result.data}} exists, then {{root.result.data.something}} is valid
        // This matches the logic in isValidVariable() for consistency
        let isNestedOnKnownParent = false;
        for (const parentVar of availableVarIds) {
          if (varRef.startsWith(parentVar.slice(0, -2) + ".")) {
            isNestedOnKnownParent = true;
            break;
          }
        }
        if (isNestedOnKnownParent) {
          return; // Valid - nested access on known parent
        }

        // Allow nested access on loop variables (e.g., {{$item.name}}, {{$item.user.id}})
        if (forEachAncestor) {
          const varContent = varRef.slice(2, -2); // Remove {{ and }}
          if (varContent.startsWith(itemVar + ".") || varContent.startsWith(indexVar + ".")) {
            return; // Valid - loop variable with nested access
          }
        }

        // Allow $parent_item/$parent_index nested access (nested for-each)
        if (forEachAncestors.length >= 2) {
          const varContent = varRef.slice(2, -2);
          if (varContent.startsWith("$parent_item") || varContent.startsWith("$parent_index")) {
            return;
          }
        }

        // Allow $for.{nodeID}.item/index references (ancestor for-each access)
        {
          const varContent = varRef.slice(2, -2);
          if (varContent.startsWith("$for.")) {
            return;
          }
        }

        // Invalid variable
        invalidVariables.push({
          variable: varRef,
          nodeId: node.id,
          nodeName,
          fieldName,
        });
      });
    });
  });

  return invalidVariables;
}
